import { request } from '@/utils/http'
import type { AxiosRequestConfig } from 'axios'
import type {
  BusinessLicenseFileInfoVO,
  EnterpriseBankAccountVO,
  EnterpriseContactVO, EnterpriseDedicatedRelVO, EnterpriseFreightLevelConfigVO,
  EnterpriseInvoiceInfoVO,
  EnterpriseUserRelVO
} from '@api/enterprise-center'
import type { ClientRoleVO } from '@api/role-permission'

// ===================== 类型定义 =====================

type RequestConfigWithErrorMessage = AxiosRequestConfig & { showErrorMessage?: boolean }

/** 企业分页查询参数 */
export interface EnterprisePageRequest {
  pageNum: number
  pageSize: number
  keyword?: string
  status?: number  // 0-待审核 1-正常 2-审核拒绝 3-禁用
}

/** 企业联系人 */
export interface EnterpriseContact {
  id: string
  contactName: string
  contactPosition?: string
  contactPhone: string
  contactEmail?: string
  ifDefault?: boolean
  ifReconciliation?: boolean
}

/** 企业分页返回单项 */
export interface EnterprisePageItem {
  enterpriseId: string
  /** 企业主表来源：manual 为手工维护，mdm 为外部同步。 */
  externalSource?: string
  enterpriseCode: string
  enterpriseName: string
  enterpriseNameEn?: string
  businessLicense: string
  contacts: EnterpriseContact[]
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  addressDetail?: string
  status: number  // 0-待审核 1-正常 2-审核拒绝 3-禁用
  lastActiveTime?: string
  userCount: number
  dateCreated: string
  remark?: string
}

/** 企业分页返回结构 */
export interface EnterprisePageResponse {
  current: number
  size: number
  total: number
  list: EnterprisePageItem[]
}

/** 运输委托单可选择的供应商企业主数据。 */
export interface SupplierEnterpriseOption {
  enterpriseId: string
  supplierCode?: string
  enterpriseCode?: string
  supplierShortName?: string
  supplierName: string
  supplierNameEn?: string
}

/** 供应商企业候选分页查询参数。 */
export interface SupplierEnterpriseOptionPageRequest {
  pageNum: number
  pageSize: number
  keyword?: string
}

/** 供应商企业候选分页结果。 */
export interface SupplierEnterpriseOptionPageResponse {
  current: number
  size: number
  total: number
  list: SupplierEnterpriseOption[]
}

/** 发货申请客户候选项 */
export interface CustomerEnterpriseOption {
  enterpriseId: string
  enterpriseCode?: string
  counterpartyCode?: string
  customerShortName?: string
  customerName: string
  customerNameEn?: string
}

/** 发货申请客户候选分页查询参数 */
export interface CustomerEnterpriseOptionPageRequest {
  pageNum: number
  pageSize: number
  keyword?: string
}

/** 发货申请客户候选分页结果 */
export interface CustomerEnterpriseOptionPageResponse {
  current: number
  size: number
  total: number
  list: CustomerEnterpriseOption[]
}

/** 企业联系人搜索字段 */
export type EnterpriseContactSearchType = 'NAME' | 'PHONE'

/** 企业联系人分页查询参数 */
export interface EnterpriseContactPageRequest {
  pageNum: number
  pageSize: number
  keyword?: string
  searchType: EnterpriseContactSearchType
}

/** 企业联系人分页返回单项 */
export interface EnterpriseContactPageItem {
  id: string
  enterpriseId: string
  enterpriseCode?: string
  enterpriseName?: string
  enterpriseNameEn?: string
  countryCode?: string
  contactName?: string
  contactPhone?: string
  ifDefault?: boolean
}

/** 企业联系人分页返回结构 */
export interface EnterpriseContactPageResponse {
  current: number
  size: number
  total: number
  list: EnterpriseContactPageItem[]
}

/** 企业详情参数 */
export interface EnterpriseDetailRequest {
  enterpriseId: string
}

/** 按企业ID查询联系人参数 */
export interface EnterpriseContactByEnterpriseRequest {
  enterpriseId: string
  keyword?: string
}

/** 企业详情返回结构 */
export interface EnterpriseDetailVO {
  detailSourceEnabled?: boolean
  id: string
  /** 企业主表来源；启用明细来源模式后，联系人及财务各用本行来源。 */
  externalSource?: string
  enterpriseCode?: string
  status: number
  enterpriseName: string
  enterpriseNameEn: string
  businessLicense: string
  businessLicenseFile?: string
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  familyContinent?: string
  familyContinentCn?: string
  familyRegion?: string
  familyRegionCn?: string
  familyCountry?: string
  familyCountryCn?: string
  familyProvince?: string
  familyProvinceCn?: string
  familyCity?: string
  familyCityCn?: string
  addressDetail: string
  remark?: string
  enterpriseType: number
  provinceName?: string
  provinceNameEn?: string
  fullAddress?: string
  fullAddressEn?: string
  contacts?: EnterpriseContactVO[]
  matchedSupplierContacts?: EnterpriseContactVO[]
  userRelList?: EnterpriseUserRelVO[]
  invoiceInfo?: EnterpriseInvoiceInfoVO
  cnyBankAccount?: EnterpriseBankAccountVO
  usdBankAccount?: EnterpriseBankAccountVO
  reconciliationContact?: EnterpriseContactVO
  freightLevelConfigList?: EnterpriseFreightLevelConfigVO[]
  dedicatedRelList?: EnterpriseDedicatedRelVO[]
  roleType?: number
  dateCreated: string,
  auditReason: string,
  businessLicenseFileInfo?: BusinessLicenseFileInfoVO,
  roleList: ClientRoleVO[],
  supplierEvaluationCard?: SupplierEvaluationCardVO
}

/**
 * 供应商评价卡片VO
 */
export interface SupplierEvaluationCardVO {
  /** 总评分（100分制） */
  totalScore?: string
  /** 评价项明细评分Map，key为评价项名称，value为该项评分（5分制），由actualScore/maxScore*5计算得出，保留1位小数 */
  itemRatings?: Record<string, string>
  /** 评价结果：引进评价(1-引进，2-储备，3-不引进)，使用评级(1-优秀，2-良好，3-合作，4-限时整改) */
  evaluationResult?: number
}

/**
 * 企业联系人请求参数
 */
export interface EnterpriseContactRequest {
  id?: string
  enterpriseId?: string
  action?: string
  contactName: string
  contactPosition?: string
  contactPhone: string
  contactEmail?: string
  ifReconciliation?: boolean
  ifDefault?: boolean
  responsibilityEnable?: boolean
  containerLoadTypes?: string[]
  loadingPortIds?: string[]
  routeIds?: string[]
  carrierIds?: string[]
}

/**
 * 企业用户关联操作请求参数
 */
export interface EnterpriseUserRelActionRequest {
  enterpriseId?: string
  userId?: string
  roleType?: number
  action?: string // 新增-ADD, 删除-DELETE, 设置管理员-UPDATE
}

/**
 * 企业开票信息请求参数
 */
export interface EnterpriseInvoiceInfoRequest {
  invoiceCompanyName?: string
  invoiceTaxNo?: string
  invoiceAddress?: string
  invoicePhone?: string
  invoiceBank?: string
  invoiceBankAccount?: string
}

/**
 * 企业银行账户请求参数
 */
export interface EnterpriseBankAccountRequest {
  bankName?: string
  bankAccount?: string
  accountName?: string
  swiftCode?: string
  bankAddress?: string
}

/**
 * 企业对接信息请求参数
 */
export interface EnterpriseDedicatedRelRequest {
  userId?: string
  dedicatedType?: number
}

/**
 * 企业运价等级配置请求参数
 */
export interface EnterpriseFreightLevelConfigRequest {
  businessType?: string
  freightLevel?: number
}

/**
 * 创建或编辑企业参数（合并）
 */
export interface EnterpriseCreateOrUpdateRequest {
  detailChanges?: import('./mdm-enterprise').MdmEnterpriseDetailsSaveRequest
  enterpriseId?: string
  enterpriseName: string
  enterpriseNameEn?: string
  enterpriseCode?: string
  businessLicense: string
  businessLicenseFile?: string
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  addressDetail?: string
  remark?: string
  contacts: EnterpriseContactRequest[]
  userRelAction?: EnterpriseUserRelActionRequest[]
  invoiceInfo?: EnterpriseInvoiceInfoRequest
  cnyBankAccount?: EnterpriseBankAccountRequest
  usdBankAccount?: EnterpriseBankAccountRequest
  dedicatedRelList?: EnterpriseDedicatedRelRequest[]
  freightLevelConfigList?: EnterpriseFreightLevelConfigRequest[]
  roleIds?: string[]
}

/** 企业状态统计返回结构 */
export interface EnterpriseStatusCountVO {
  total: number
  normal: number
  disabled: number
  pending: number
  rejected: number
}

/** 企业审核参数 */
export interface AuditEnterpriseParams {
  enterpriseId: string
  auditResult: boolean
  auditReason?: string
}


/** 企业信息 */
export interface SimpleEnterpriseVO {
  enterpriseId: string
  enterpriseName: string
  enterpriseNameEn: string
  countryCode?: string
}

/**
 * 修改企业状态
 */
export interface UpdateEnterpriseStatusRequest {
  enterpriseId: string
  status: number
}

/** 用户详情VO */
export interface UserDetailVO {
  id: string
  username: string
  phone: string
  email: string
  status: number // 1-启用 0-禁用
  dateCreated: string
  loginTime: string
}

/** api接口配置 */
export interface EnterpriseApiConfig {
  id: number
  accessKey: string
  secretKey: string
  callbackUrl: string
  whitelistIps: []
  enable: boolean
}

export interface EnterpriseApiConfigCreateOrUpdateRequest {
  id: number
  accessKey: string
  secretKey: string
  callbackUrl: string
  whitelistIps: []
  enable: boolean
  ipAction: boolean
  envType?: string
}

/**
 * 通过联系人职责匹配供应商请求参数
 * 与后端 SupplierMatchByResponsibilityRequest 对齐：carrierIds / routeIds 优先于单值
 */
export interface SupplierMatchByResponsibilityRequest {
  /** 整拼类型: FCL_EXPORT/LCL_EXPORT/AIR_EXPORT/RAIL_EXPORT/PRE_PORT/LAST_MILE/RAIL/EXPRESS 等 */
  containerLoadType: string
  /** 起运港ID */
  loadingPortId?: string
  /** @deprecated 历史字段，请用 loadingPortId */
  loadingPortIds?: string
  /** 航线ID（单值，与 routeIds 互斥，后者优先） */
  routeId?: string
  /** 航线ID列表（多选，优先于 routeId） */
  routeIds?: string[]
  /** 船公司ID（单值，与 carrierIds 互斥，后者优先） */
  carrierId?: string
  /** 船公司ID列表（多选，优先于 carrierId） */
  carrierIds?: string[]
}

// ===================== 接口实现 =====================

/**
 * 企业分页查询
 */
export const fetchEnterprisePage = (params: EnterprisePageRequest) => {
  return request.post<EnterprisePageResponse>('/enterprise/tenant/clientEnterprise/page', params)
}

/**
 * 查询当前租户的供应商企业候选，支持编码、简称和名称模糊匹配。
 */
export const fetchSupplierEnterpriseOptions = (params: SupplierEnterpriseOptionPageRequest) => {
  return request.post<SupplierEnterpriseOptionPageResponse>(
    '/enterprise/tenant/clientEnterprise/supplier/options',
    params
  )
}

/**
 * 查询发货申请可选客户，支持简称、名称、企业编码和相对方代号模糊匹配。
 */
export const fetchCustomerEnterpriseOptions = (params: CustomerEnterpriseOptionPageRequest) => {
  return request.post<CustomerEnterpriseOptionPageResponse>(
    '/enterprise/tenant/clientEnterprise/customer/options',
    params
  )
}

/**
 * 分页查询当前租户关联企业的联系人。
 */
export const fetchEnterpriseContactPage = (params: EnterpriseContactPageRequest) => {
  return request.post<EnterpriseContactPageResponse>(
    '/enterprise/tenant/clientEnterprise/contact/page',
    params
  )
}

/**
 * 企业详情
 */
export const fetchEnterpriseDetail = (
  params: EnterpriseDetailRequest,
  config?: { showErrorMessage?: boolean }
) => {
  return request.post<EnterpriseDetailVO>(
    '/enterprise/tenant/clientEnterprise/detail',
    params,
    { showErrorMessage: config?.showErrorMessage } as RequestConfigWithErrorMessage
  )
}

/**
 * 按企业ID查询企业联系人
 */
export const fetchEnterpriseContactsByEnterpriseId = (
  params: EnterpriseContactByEnterpriseRequest,
  config?: { showErrorMessage?: boolean }
) => {
  return request.post<EnterpriseContactVO[]>(
    '/enterprise/client/contact/listByEnterpriseId',
    params,
    { showErrorMessage: config?.showErrorMessage } as RequestConfigWithErrorMessage
  )
}

/**
 * 删除企业
 */
export const deleteEnterprise = (params: { enterpriseId: string }) => {
  return request.post<boolean>('/enterprise/tenant/clientEnterprise/delete', params)
}

/**
 * 企业状态统计
 */
export const fetchEnterpriseStatusCount = () => {
  return request.post<EnterpriseStatusCountVO>('/enterprise/tenant/clientEnterprise/enterpriseStatusCount')
}

/**
 * 企业审核接口
 */
export const auditEnterprise = (params: AuditEnterpriseParams) => {
  return request.post<boolean>('/enterprise/tenant/clientEnterprise/audit', params)
}

/**
 * 修改企业状态
 */
export const updateEnterpriseStatus = (params: UpdateEnterpriseStatusRequest) => {
  return request.post<boolean>('/enterprise/tenant/clientEnterprise/status', params)
}

/**
 * 平台端创建企业
 */
export const createEnterprise = (params: EnterpriseCreateOrUpdateRequest) => {
  return request.post<number>('/enterprise/tenant/clientEnterprise/create', params)
}

/**
 * 平台端更新企业
 */
export const updateEnterprise = (params: EnterpriseCreateOrUpdateRequest) => {
  return request.post<any>('/enterprise/tenant/clientEnterprise/update', params, {
    showErrorMessage: false
  } as RequestConfigWithErrorMessage)
}

/**
 * 获取租户下所有客户企业
 */
/**
 * 查询企业财务信息（多开票主子表），含发票抬头列表和银行账户
 */
export const fetchEnterpriseFinancial = (enterpriseId: string) => {
  return request.get<any>(`/enterprise/tenant/mdm/enterprise/${enterpriseId}/financial`)
}

export const listCustomerEnterpriseByTenant  = () => {
  return request.post<SimpleEnterpriseVO[]>('/enterprise/tenant/clientEnterprise/listCustomerEnterpriseByTenant')
}

/**
 * 获取租户下所有用户
 */
export const listUserByTenant = () => {
  return request.post<UserDetailVO[]>('/enterprise/tenant/clientEnterprise/listUserByTenant')
}

/**
 * 获取API配置信息
 */
export const getEnterpriseApiConfig = (params: any) => {
  return request.get<EnterpriseApiConfig>(`/enterprise/tenant/apiConfig?envType=${  params}`)
}
/**
 * 保存API配置信息
 */
export const saveEnterpriseApiConfig = (params: EnterpriseApiConfigCreateOrUpdateRequest) => {
  return request.post<any>('/enterprise/tenant/apiConfig', params)
}

/**
 * 重置API配置SK信息
 */
export const resetEnterpriseApi = (id:number) => {
  return request.get<any>(`/enterprise/tenant/apiConfig/reset/${  id}`)
}

/**
 * 按环境重置 SK；无配置时后端会先初始化
 */
export const resetEnterpriseApiByEnv = (envType: string) => {
  return request.get<EnterpriseApiConfig>(`/enterprise/tenant/apiConfig/resetByEnv?envType=${envType}`)
}

/**
 * 通过联系人职责匹配供应商
 * @param params 匹配条件(整拼类型、航线、船公司)
 * @returns 匹配的供应商企业列表
 */
export const matchSuppliersByResponsibility = (params: SupplierMatchByResponsibilityRequest) => {
  return request.post<EnterpriseDetailVO[]>('/enterprise/tenant/clientEnterprise/matchSuppliersByResponsibility', params)
}
